import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import useViewerIdentity from '../../hooks/useViewerIdentity';
import Button from '../common/ui/Button';
import './NotificationBell.css';

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function getActionText(n) {
  switch (n.type) {
    case 'COMMENT_REPLY': return 'replied to your comment';
    case 'COMMENT_LIKE': return 'liked your comment';
    case 'NOVEL_COMMENT': return 'commented on your novel';
    case 'CHAPTER_COMMENT': return 'commented on your chapter';
    case 'REVIEW_LIKE': return 'liked your review';
    case 'DISCUSSION_POST_UPVOTE': return 'upvoted your discussion post';
    case 'DISCUSSION_COMMENT_LIKE': return 'liked your discussion comment';
    case 'DISCUSSION_COMMENT_REPLY': return 'replied to your discussion comment';
    case 'DISCUSSION_POST_COMMENT': return 'commented on your post';
    case 'NOVEL_DISCUSSION_CREATED': return 'started a discussion about your novel';
    case 'DISCUSSION_THREAD_ACTIVITY': return 'commented in a thread you follow';
    case 'CONTEST_JUDGE_INVITE': return 'invited you to judge a contest';
    case 'BADGE_AWARDED': return 'You earned a new badge!';
    case 'NEW_CHAPTER_FOLLOWED_AUTHOR': return 'published a new chapter';
    case 'CHARACTER_SUGGESTION': return 'suggested an edit on a character in your novel';
    case 'NOVEL_COMMENT_DELETED': return 'removed your novel comment';
    case 'DISCUSSION_COMMENT_DELETED': return 'removed your discussion comment';
    case 'DISCUSSION_POST_DELETED': return 'removed your discussion post';
    case 'REVIEW_DELETED': return 'removed your review';
    case 'NOVEL_FLAGGED_FAN_FICTION': return 'flagged your novel as fan fiction';
    case 'NOVEL_FAN_FICTION_FLAG_LIFTED': return 'lifted the fan fiction flag on your novel';
    case 'NEW_REVIEW': return 'reviewed your novel';
    case 'REVIEW_AUTHOR_REPLY': return 'replied to your review';
    case 'MENTION': return n.postId && !n.commentId ? 'mentioned you in a post' : 'mentioned you in a comment';
    case 'ADMIN_ANNOUNCEMENT': return n.postTitle || 'New announcement';
    case 'SPAM_ALERT': return 'was flagged for spam — review the alert queue';
    default: return 'interacted with your content';
  }
}

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useViewerIdentity();
  const { notifications, unreadCount, markAsRead, markAllRead } = useNotifications();

  const displayCount = unreadCount > 99 ? '99+' : unreadCount;

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check both the bell button and the portal-rendered dropdown
      const clickedBell = bellRef.current && bellRef.current.contains(e.target);
      const clickedDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!clickedBell && !clickedDropdown) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    setIsOpen(false);
    if (notification.type === 'ADMIN_ANNOUNCEMENT' && notification.linkUrl) {
      if (notification.linkUrl.startsWith('/')) {
        navigate(notification.linkUrl);
      } else {
        window.open(notification.linkUrl, '_blank', 'noopener,noreferrer');
      }
    } else if (notification.type === 'SPAM_ALERT') {
      // Deep-link to the spam-alerts queue on the moderator dashboard.
      navigate(notification.linkUrl || '/moderator/dashboard#spam-alerts');
    } else if (notification.type === 'BADGE_AWARDED') {
      if (user?.id) {
        navigate(`/user/${user.id}?tab=badges`);
      }
    } else if (notification.type === 'CHARACTER_SUGGESTION' && notification.novelId) {
      navigate(`/edit-novel/${notification.novelId}?tab=characters`);
    } else if (notification.contestId) {
      navigate(`/contests/${notification.contestId}`);
    } else if (notification.postId) {
      navigate(`/discussions/${notification.postId}${notification.commentId ? `?comment=${notification.commentId}` : ''}`);
    } else if (notification.chapterNumber !== null && notification.chapterNumber !== undefined) {
      navigate(`/novels/${notification.novelId}/chapters/${notification.chapterNumber}`);
    } else if (notification.novelId) {
      navigate(`/novels/${notification.novelId}`);
    }
  };

  const handleMarkAllRead = () => {
    markAllRead();
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/profile?tab=notifications');
  };

  const visibleNotifications = notifications.slice(0, 8);

  // Compute dropdown position from the bell button
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  const updatePosition = useCallback(() => {
    if (bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      const mobile = window.innerWidth <= 640;
      setIsMobile(mobile);
      if (mobile) {
        setDropdownStyle({
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%',
          maxHeight: '70vh',
        });
      } else {
        setDropdownStyle({
          position: 'fixed',
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right - 8,
          width: 380,
          maxHeight: 480,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      // Lock body scroll on mobile
      if (window.innerWidth <= 640) {
        document.body.style.overflow = 'hidden';
      }
    }
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.body.style.overflow = '';
    };
  }, [isOpen, updatePosition]);

  return (
    <div className="notif-bell" ref={bellRef}>
      <Button
        variant="ghost"
        className="notif-bell__btn"
        onClick={handleToggle}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={isOpen}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notif-bell__badge">{displayCount}</span>
        )}
      </Button>

      {isOpen && createPortal(
        <>
          {isMobile && <div className="notif-bell__backdrop" onClick={() => setIsOpen(false)} />}
          <div className="notif-bell__dropdown" ref={dropdownRef} role="menu" style={dropdownStyle}>
          <div className="notif-bell__dropdown-header">
            <span className="notif-bell__dropdown-title">Notifications</span>
            {unreadCount > 0 && (
              <button className="notif-bell__mark-all" onClick={handleMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="notif-bell__dropdown-list">
            {visibleNotifications.length === 0 ? (
              <div className="notif-bell__empty">
                <p>No notifications yet</p>
              </div>
            ) : (
              visibleNotifications.map((n) => (
                <button
                  key={n.id}
                  className={`notif-bell__item ${!n.isRead ? 'notif-bell__item--unread' : ''} ${n.type === 'BADGE_AWARDED' ? 'notif-bell__item--badge' : ''}`}
                  onClick={() => handleNotificationClick(n)}
                  role="menuitem"
                >
                  <div className="notif-bell__item-avatar">
                    {n.type === 'BADGE_AWARDED' ? (
                      <span className="notif-bell__item-initials notif-bell__item-initials--badge">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="8" r="6" />
                          <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
                        </svg>
                      </span>
                    ) : n.type === 'ADMIN_ANNOUNCEMENT' ? (
                      <span className="notif-bell__item-initials notif-bell__item-initials--badge">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 11l18-5v12L3 14v-3z" />
                          <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
                        </svg>
                      </span>
                    ) : n.actorProfileImageUrl ? (
                      <img src={n.actorProfileImageUrl} alt={n.actorUsername} onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span className="notif-bell__item-initials">
                        {(n.actorUsername || '?').slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="notif-bell__item-content">
                    <p className="notif-bell__item-text">
                      {n.type === 'BADGE_AWARDED' || n.type === 'ADMIN_ANNOUNCEMENT' ? (
                        <>{getActionText(n)}</>
                      ) : (
                        <>
                          <strong><Link to={`/user/${n.actorId}`} onClick={(e) => e.stopPropagation()} className="notif-actor-link">{n.actorUsername || n.actorFullName}</Link></strong>{' '}
                          {getActionText(n)}
                        </>
                      )}
                    </p>
                    {n.commentPreview && (
                      <p className="notif-bell__item-preview">"{n.commentPreview}"</p>
                    )}
                    {n.reason && (
                      <p className="notif-bell__item-reason">{n.type === 'ADMIN_ANNOUNCEMENT' ? n.reason : `Reason: ${n.reason}`}</p>
                    )}
                    {n.novelTitle && (
                      <p className="notif-bell__item-novel">{n.novelTitle}</p>
                    )}
                    {n.postTitle && n.type !== 'ADMIN_ANNOUNCEMENT' && (
                      <p className="notif-bell__item-novel">{n.postTitle}</p>
                    )}
                    <span className="notif-bell__item-time">{formatRelativeTime(n.createdAt)}</span>
                  </div>
                  {!n.isRead && <span className="notif-bell__item-dot" />}
                </button>
              ))
            )}
          </div>

          <div className="notif-bell__dropdown-footer">
            <button className="notif-bell__view-all" onClick={handleViewAll}>
              View all notifications
            </button>
          </div>
        </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
